import { Injectable, signal, computed, inject, NgZone } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { debounceTime, map, share, skip } from 'rxjs/operators';
import { Observable, fromEvent } from 'rxjs';

/**
 * SOURCE OF TRUTH: One place to define widths.
 * These match the standard Angular Material (CDK) Breakpoints.
 */
const WIDTHS = {
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};

/**
 * LOGIC MAP: Uses the WIDTHS constants to define every alias.
 */
const BREAKPOINT_MAP: Record<string, (w: number) => boolean> = {
  xs: (w) => w < WIDTHS.sm,
  sm: (w) => w >= WIDTHS.sm && w < WIDTHS.md,
  md: (w) => w >= WIDTHS.md && w < WIDTHS.lg,
  lg: (w) => w >= WIDTHS.lg && w < WIDTHS.xl,
  xl: (w) => w >= WIDTHS.xl,
  'lt-sm': (w) => w < WIDTHS.sm,
  'lt-md': (w) => w < WIDTHS.md,
  'lt-lg': (w) => w < WIDTHS.lg,
  'lt-xl': (w) => w < WIDTHS.xl,
  'gt-xs': (w) => w >= WIDTHS.sm,
  'gt-sm': (w) => w >= WIDTHS.md,
  'gt-md': (w) => w >= WIDTHS.lg,
  'gt-lg': (w) => w >= WIDTHS.xl,
};

export enum ViewportClass {
  EXTRA_SMALL = 'xs',
  SMALL = 'sm',
  MEDIUM = 'md',
  LARGE = 'lg',
  EXTRA_LARGE = 'xl',
  EXTRA_EXTRA_LARGE = 'xxl',
}

export interface ViewportInfo {
  width: number;
  height: number;
  viewportClass: ViewportClass;
  alias: string;
}

@Injectable({ providedIn: 'root' })
export class ViewportSignalService {
  private readonly zone = inject(NgZone);
  private readonly bp = inject(BreakpointObserver);

  // 1. Primary Signal
  readonly viewport = signal<ViewportInfo>(this.snapshot());

  // 2. Computed Aliases (Checks the WIDTHS logic reactively)
  private readonly activeAliases = computed(() => {
    const w = this.viewport().width;
    const active = new Set<string>();
    for (const [alias, test] of Object.entries(BREAKPOINT_MAP)) {
      if (test(w)) active.add(alias);
    }
    return active;
  });

  // 3. Derived Layout Signals
  readonly isMobile = computed(() => this.viewport().width < WIDTHS.sm);
  readonly isTablet = computed(() => this.viewport().width >= WIDTHS.sm && this.viewport().width < WIDTHS.md);
  readonly isDesktop = computed(() => this.viewport().width >= WIDTHS.md);

  // Live window size, updated on every (debounced) resize — unlike `viewport`, which only
  // updates on breakpoint crossings. Consumers just read these signals directly; there's no
  // need to subscribe to `onResize` and manually trigger change detection, because a signal
  // write notifies its template consumers through Angular's reactive graph regardless of which
  // zone the write happens in (see NotificationSource.MarkAncestorsForTraversal) — that
  // notification only walks the ancestor path of whatever actually reads the signal, unlike
  // NgZone.run(), which schedules a full app-wide tick.
  readonly windowWidth = signal(window.innerWidth);
  readonly windowHeight = signal(window.innerHeight);

  readonly onResize: Observable<{ width: number; height: number }> = this.zone.runOutsideAngular(() =>
    fromEvent(window, 'resize').pipe(
      debounceTime(20),
      map(() => ({ width: window.innerWidth, height: window.innerHeight })),
      share(),
    ),
  );

  constructor() {
    this.zone.runOutsideAngular(() => {
      // Use the standard CDK breakpoints for the hardware-level listener
      this.bp
        .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
        .pipe(takeUntilDestroyed())
        .subscribe(() => {
          this.zone.run(() => this.viewport.set(this.snapshot()));
        });

      // `fromEvent` registers its native listener lazily, at `.subscribe()` time — not when
      // `onResize` is built above — so this subscription must itself run outside the zone too,
      // or the raw `resize` listener would still be captured inside Angular's zone (zone.js
      // captures `Zone.current` at `addEventListener()` time) and every native tick, debounced
      // or not, would queue a full-app `ApplicationRef.tick()` via `onMicrotaskEmpty`. Writing
      // the signals here (without `zone.run()`) is enough for consumers to update, per the
      // targeted-traversal note on `windowWidth`/`windowHeight` above.
      this.onResize.pipe(takeUntilDestroyed()).subscribe(({ width, height }) => {
        this.windowWidth.set(width);
        this.windowHeight.set(height);
      });
    });
  }

  readonly onBreakpointChange = toObservable(this.viewport).pipe(skip(1));

  isActive(bp: string | string[]): boolean {
    const aliases = Array.isArray(bp) ? bp : [bp];
    const currentActive = this.activeAliases();
    return aliases.some((alias) => currentActive.has(alias));
  }

  private snapshot(): ViewportInfo {
    const w = window.innerWidth;
    return {
      width: w,
      height: window.innerHeight,
      viewportClass: this.classify(w),
      alias: this.classify(w), // We can reuse classify to get the alias string
    };
  }

  private classify(w: number): ViewportClass {
    if (w < WIDTHS.sm) return ViewportClass.EXTRA_SMALL;
    if (w < WIDTHS.md) return ViewportClass.SMALL;
    if (w < WIDTHS.lg) return ViewportClass.MEDIUM;
    if (w < WIDTHS.xl) return ViewportClass.LARGE;
    return ViewportClass.EXTRA_LARGE;
  }
}
